import React, { useState, useEffect } from "react";
import Axios from "axios";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import StyledDialog from "../StyledDialog";
import TeacherSelect from "../TeacherSelect";
import ThumbnailField from "../ThumbnailField";
import PdfUploadField from "../PdfUploadField";
import ClassificationCascade from "../ClassificationCascade";
import { usePdfUpload } from "../../hooks/usePdfUpload";
import { buildNormalLectureFileName } from "../../lib/pdfFileName";
import { dateChange } from "../../lib/dates";
import { URL_TYPE_OPTIONS } from "../../constants";
import { ApiUrlAdmin } from "../../../../api/common/Config";
import ResponseStatus from "../../../../util/enums";

// "Normal Ders Ekle" — curriculum-tied lecture creation.
// Progressive disclosure (cascade → teacher → date → thumbnail → rest) and the
// three submit gates (PDF uploaded+verified, thumbnail, checked notes) are
// preserved legacy controls. The modal is only mounted while open, so closing
// it discards all transient state (the legacy reset matrix).
//
// duplicateSource (optional): an existing lecture to copy — text inputs,
// teacher, public flag and url type are prefilled; the cascade is prefilled by
// the container via catalog.prefillFromLecture; the source PDF is adopted as
// already-uploaded and the thumbnail is fetched back into a File. The date is
// deliberately NOT copied (it's the one field a duplicate exists to change),
// and the attestation checkbox still starts unchecked.
const AddLectureModal = ({
    open,
    onClose,
    teachers,
    catalog,
    adminToken,
    urlType,
    onUrlTypeChange,
    onAdded,
    duplicateSource,
}) => {
    const pdf = usePdfUpload(adminToken);
    const source = duplicateSource || null;
    const sourceSubTopicName =
        source && source.SubTopicName && source.SubTopicName.includes("/")
            ? source.SubTopicName.split("/")[1].trim()
            : "";
    const [selectedTeacher, setSelectedTeacher] = useState(
        source && source.TeacherInfos ? String(source.TeacherInfos.TeacherId) : ""
    );
    const [startDate, setStartDate] = useState(new Date());
    const [thumbnailFile, setThumbnailFile] = useState(null);
    const [thumbnailPreview, setThumbnailPreview] = useState(null);
    const [isPublic, setIsPublic] = useState(source ? Boolean(source.IsPublic) : false);
    const [subTopicName, setSubTopicName] = useState(sourceSubTopicName);
    const [streamUrl, setStreamUrl] = useState(source ? source.StreamUrl || "" : "");
    const [offlineUrl, setOfflineUrl] = useState(source ? source.OfflineUrl || "" : "");
    // Local so a duplicate can start from the source's url type; user changes
    // still persist to the sticky page default via onUrlTypeChange
    const [urlTypeValue, setUrlTypeValue] = useState(
        source && source.OfflineUrlType != null ? source.OfflineUrlType : urlType
    );

    useEffect(() => {
        if (!source) return;
        if (source.LectureNoteUrl) {
            pdf.seedAsUploaded(source.LectureNoteUrl);
        }
        if (source.ThumbnailUrl) {
            let cancelled = false;
            fetch(source.ThumbnailUrl)
                .then((res) => (res.ok ? res.blob() : Promise.reject(new Error("thumbnail fetch failed"))))
                .then((blob) => {
                    if (cancelled) return;
                    const file = new File([blob], "thumbnail.png", { type: blob.type || "image/png" });
                    setThumbnailFile(file);
                    setThumbnailPreview(URL.createObjectURL(file));
                })
                .catch(() => {
                    // CORS or network failure: leave empty — the thumbnail gate
                    // will require the operator to pick one manually
                });
            return () => {
                cancelled = true;
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const pdfFileName = () =>
        buildNormalLectureFileName({
            examTypeName: catalog.selectedSegment ? catalog.selectedSegment.ExamTypeName : "",
            courseName: catalog.selectedCourse ? catalog.selectedCourse.Name : "",
            topicName: catalog.selectedTopic ? catalog.selectedTopic.Name : "",
            subTopicName,
        });

    const handleSubmit = async (event) => {
        event.preventDefault();

        // Gate 1+2: a PDF must exist AND its upload must have succeeded
        if (!pdf.pdfUrl || !pdf.isUploadedOk) {
            toast.error("pdf eklenmeden Ders ekleyemezsiniz");
            return;
        }
        // Gate 3: thumbnail required
        if (!thumbnailPreview) {
            toast.error("Thumbnail eklenmeden Ders ekleyemezsiniz");
            return;
        }

        const formData = new FormData();
        formData.append("SubTopicName", subTopicName);
        formData.append("StreamUrl", streamUrl);
        formData.append("OfflineUrl", offlineUrl);
        if (isPublic) formData.append("PublicStream", "on");
        formData.append("SubCourseTopicUniqueId", Number(catalog.selectedTopicId));
        formData.append("ExamCategoryId", Number(catalog.examCategoryRadioValue));
        formData.append("OnlineLectureTeacherId", Number(selectedTeacher));
        formData.append("CourseId", Number(catalog.selectedCourseId));
        formData.append("ExamType", Number(catalog.selectedSegment ? catalog.selectedSegment.ExamTypeId : 0));
        formData.append("ChannelCode", "Web");
        formData.append("Date", dateChange(startDate));
        formData.append("ThumbnailUrl", thumbnailFile);
        formData.append("LectureNoteUrl", pdf.pdfUrl);
        formData.append("RowStatus", true);
        formData.append("IsPublic", isPublic);
        formData.append("OfflineUrlType", urlTypeValue);

        const headers = {
            "Access-Control-Allow-Origin": "*",
            Authorization: "Bearer " + adminToken,
            "Content-Type": "multipart/form-data",
        };
        try {
            const res = await Axios.post(ApiUrlAdmin + "OnlineLecture/addLecture", formData, { headers });
            if (res.status == 200) {
                if (res.data.ResponseStatus == ResponseStatus.Success) {
                    toast.success("Ders başarıyla eklenmiştir");
                    onAdded();
                } else if (res.data.ResponseStatus == ResponseStatus.Error) {
                    toast.error(res.data.ResponseMessage);
                }
            } else {
                toast.error("bir hata ile karşılaşıldı lütfen tekrar deneyiniz");
            }
        } catch (ex) {
            toast.error(ex.response && ex.response.data ? ex.response.data.Error : "Bir hata oluştu");
        }
        onClose();
    };

    return (
        <StyledDialog open={open} onClose={onClose}>
            <div className="olp-theme olp-modal">
                <h2 className="olp-modal-title">{source ? "Dersi Çoğalt" : "Normal Ders Ekle"}</h2>
                <p className="olp-modal-subtitle">
                    {source
                        ? "Bilgiler kaynak dersten kopyalandı — yeni tarihi seçmeniz yeterli"
                        : "Müfredata bağlı canlı ders oluşturun"}
                </p>
                <form method="post" onSubmit={handleSubmit}>
                    <div className="olp-modal-section">
                        <p className="olp-modal-section-title">Sınıflandırma</p>
                        <ClassificationCascade catalog={catalog} onMetadataChange={() => pdf.invalidate(true)} />
                    </div>
                    {catalog.selectedTopicId && (
                        <div className="olp-modal-section">
                            <p className="olp-modal-section-title">Öğretmen ve Tarih</p>
                            <div className="olp-field">
                                <label>Öğretmen Seçiniz: </label>
                                <TeacherSelect
                                    teachers={teachers}
                                    value={selectedTeacher}
                                    onChange={(id) => setSelectedTeacher(String(id))}
                                />
                            </div>
                            {selectedTeacher && (
                                <div className="olp-datepicker">
                                    <DatePicker
                                        selected={startDate}
                                        onChange={(date) => setStartDate(date)}
                                        timeInputLabel="Time:"
                                        dateFormat="MM/dd/yyyy h:mm aa"
                                        showTimeInput
                                        inline
                                    />
                                </div>
                            )}
                            <ThumbnailField
                                previewUrl={thumbnailPreview}
                                onSelect={(file, objectUrl) => {
                                    setThumbnailFile(file);
                                    setThumbnailPreview(objectUrl);
                                }}
                            />
                        </div>
                    )}
                    {catalog.selectedTopicId && thumbnailPreview && (
                        <div className="olp-modal-section">
                            <p className="olp-modal-section-title">Ders Detayları</p>
                            <div className="olp-field olp-field--check">
                                <input
                                    type="checkbox"
                                    checked={isPublic}
                                    name="PublicStream"
                                    id="PublicStream"
                                    onChange={() => setIsPublic(!isPublic)}
                                />
                                <label htmlFor="PublicStream">Herkese açık yayın</label>
                            </div>
                            <div className="olp-field">
                                <label>Url Tipi: </label>
                                <select
                                    className="olp-select olp-select--inline"
                                    value={urlTypeValue}
                                    onChange={(e) => {
                                        const value = parseInt(e.target.value, 10);
                                        setUrlTypeValue(value);
                                        onUrlTypeChange(value);
                                    }}
                                >
                                    {URL_TYPE_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="olp-field">
                                <label htmlFor="SubTopicName">Alt Başlık Giriniz(part1,part2): </label>
                                <input
                                    className="olp-input"
                                    id="SubTopicName"
                                    name="SubTopicName"
                                    type="text"
                                    value={subTopicName}
                                    onChange={(e) => {
                                        setSubTopicName(e.target.value);
                                        pdf.invalidate(true);
                                    }}
                                />
                            </div>
                            <div className="olp-field">
                                <label htmlFor="UrlInput">Castr Url Giriniz: </label>
                                <input
                                    className="olp-input"
                                    name="StreamUrl"
                                    type="text"
                                    id="UrlInput"
                                    value={streamUrl}
                                    onChange={(e) => setStreamUrl(e.target.value)}
                                />
                            </div>
                            <div className="olp-field">
                                <label htmlFor="OfflineUrlInput">YouTube Url Giriniz: </label>
                                <input
                                    className="olp-input"
                                    required
                                    name="OfflineUrl"
                                    type="text"
                                    id="OfflineUrlInput"
                                    value={offlineUrl}
                                    onChange={(e) => setOfflineUrl(e.target.value)}
                                />
                            </div>
                            <PdfUploadField pdf={pdf} onFileSelected={(file) => pdf.upload(file, pdfFileName())} />
                        </div>
                    )}
                    <div className="olp-modal-footer">
                        <button type="button" className="olp-btn" onClick={onClose}>
                            Vazgeç
                        </button>
                        <button type="submit" className="olp-btn olp-btn--primary" disabled={pdf.submitLocked}>
                            Ders Ekle
                        </button>
                    </div>
                </form>
            </div>
        </StyledDialog>
    );
};

export default AddLectureModal;
